import type {
  ChargePointSummary,
  ConnectorFormat,
  ConnectorType,
  LoadBalancingGroupSummary as LoadBalancingGroupDTO,
  NetworkStatus,
  OcppStatus,
  PowerType,
} from '../../app/ApiGen';
import { APIErrorAndError } from '../../utils/error';
import type { Normalised } from '../../utils/request';

// Unfortunately, charge points coming from the Load Balancing Group API endpoints are JSON object strings.
export type LoadBalancingGroup = Omit<Normalised<LoadBalancingGroupDTO>, 'chargePointJSONs'> & {
  chargePoints: Normalised<ChargePointSummary>[];
};

/**
 * @deprecated Remove when Load Balancing Group endpoints return charge points as
 * DTOs rather than JSON object strings. (requires removing charge-point-service-grpc)
 */
interface LoadBalancingGroupChargePoint {
  id: string;
  ocppChargePointId: string;
  evses: Record<string, Evse>;
  tokenRequired: boolean;
  networkStatus: 'Online' | 'Offline' | 'NeverConnected';
  networkStatusUpdated: string;
  maxCurrent: number;
  updatedDate: string;
  createdDate: string;
  needsRegistrationInformation: boolean;
  timeZone: string;
  name: string;
  chargePointSerial: string;
  chargePointVendor: string;
  chargePointModel: string;
  firmwareVersion?: string;
  iccid?: string;
  electricityCost?: { costs: { start: number; cost: string }[]; currency: string; duration: number };
  chargingProfiles?: Record<string, unknown>;
  targetCombinedProfile?: Record<string, unknown>;
  username?: string;
  hashedAuthorizationKey?: string;
}

interface Evse {
  id: string;
  updatedDate: string;
  connector: {
    id: string;
    connectorType: ConnectorType;
    connectorFormat: ConnectorFormat;
    powerType: PowerType;
    maxAmperage: number;
    maxVoltage: number;
    updatedDate: string;
    meterValue?: {
      currentImport?: number;
      energyActiveImportRegister?: number;
      powerActiveImport?: number;
      stateOfCharge?: number;
      temperature?: number;
      frequency?: number;
      currentImportPhased?: Partial<Record<'L1' | 'L2' | 'L3', number>>;
      timestamp: string;
      voltage?: number;
      voltagePhased?: Partial<Record<'L1N' | 'L2N' | 'L3N', number>>;
    };
  };
  evseStatus:
    | 'Available'
    | 'Preparing'
    | 'Charging'
    | 'SuspendedEVSE'
    | 'SuspendedEV'
    | 'Finishing'
    | 'Reserved'
    | 'Unavailable'
    | 'Faulted';
  errorCode: string;
}

export interface LoadBalancingGroupState {
  error?: APIErrorAndError;
  current?: LoadBalancingGroup;
  loading: boolean;
  selectedItemId?: LoadBalancingGroup['id'];
}

/**
 * Hacked-together adapter that ignores electricityCost, chargingProfiles, and some other optional properties that are
 * missing from LoadBalancingGroupChargePoint
 */
function toNormalisedChargePoint(chargePoint: LoadBalancingGroupChargePoint): Normalised<ChargePointSummary> {
  const {
    chargePointModel,
    chargePointSerial,
    chargePointVendor,
    createdDate,
    evses,
    firmwareVersion,
    iccid,
    id,
    maxCurrent,
    name,
    networkStatus,
    networkStatusUpdated,
    ocppChargePointId,
    timeZone,
    tokenRequired,
    updatedDate,
  } = chargePoint;

  const networkStatusMap: Record<LoadBalancingGroupChargePoint['networkStatus'], NetworkStatus> = {
    NeverConnected: 'NEVER_CONNECTED',
    Offline: 'OFFLINE',
    Online: 'ONLINE',
  };

  return {
    connectors: Object.values(evses).map((evse) => {
      const {
        connector: {
          id: connectorId,
          connectorType,
          connectorFormat,
          powerType,
          updatedDate: connectorUpdated,
          meterValue,
          maxVoltage,
          maxAmperage,
        },
        errorCode,
        evseStatus,
        id: evseId,
      } = evse;

      const evseStatusMap: Record<LoadBalancingGroupChargePoint['evses'][string]['evseStatus'], OcppStatus> = {
        Available: 'AVAILABLE',
        Preparing: 'PREPARING',
        Charging: 'CHARGING',
        SuspendedEV: 'SUSPENDED_EV',
        SuspendedEVSE: 'SUSPENDED_EVSE',
        Faulted: 'FAULTED',
        Finishing: 'FINISHING',
        Reserved: 'RESERVED',
        Unavailable: 'UNAVAILABLE',
      };

      return {
        connectorFormat,
        connectorId,
        connectorType,
        evseId,
        maxAmperage,
        maxVoltage,
        meter: meterValue
          ? {
              current: meterValue.currentImport,
              currentL1: meterValue.currentImportPhased?.L1,
              currentL2: meterValue.currentImportPhased?.L2,
              currentL3: meterValue.currentImportPhased?.L3,
              frequency: meterValue.frequency,
              power: meterValue.powerActiveImport,
              register: meterValue.energyActiveImportRegister,
              stateOfCharge: meterValue.stateOfCharge,
              temperature: meterValue.temperature,
              updatedDate: meterValue.timestamp,
              voltage: meterValue.voltage,
              voltageL1N: meterValue.voltagePhased?.L1N,
              voltageL2N: meterValue.voltagePhased?.L2N,
              voltageL3N: meterValue.voltagePhased?.L3N,
            }
          : undefined,
        ocppCode: errorCode,
        ocppStatus: evseStatusMap[evseStatus],
        powerType,
        updatedDate: connectorUpdated,
      };
    }),
    createdDate,
    features: {},
    firmware: firmwareVersion,
    iccid,
    id,
    maxCurrent,
    model: chargePointModel,
    name,
    networkStatus: networkStatusMap[networkStatus],
    networkStatusUpdatedDate: networkStatusUpdated,
    ocppChargePointId,
    profiles: {},
    serial: chargePointSerial,
    timeZone,
    tokenRequired,
    updatedDate,
    vendor: chargePointVendor,
  };
}

/**
 * @deprecated No longer needed when charge points are properly included as JSON:API resources from load balancing group
 * endpoints.
 */
export function fromDTO(dto: LoadBalancingGroupDTO): LoadBalancingGroup {
  const {
    id,
    attributes: { name, phases, enabled, chargePointJSONs },
  } = dto;
  const chargePoints =
    chargePointJSONs?.flatMap((json) => {
      try {
        const parsed = JSON.parse(json) as LoadBalancingGroupChargePoint;
        return [toNormalisedChargePoint(parsed)];
      } catch (e) {
        return [];
      }
    }) ?? [];
  return { id, phases, enabled, name, chargePoints };
}
